<!--
    LP-5358 / LP-5360: redemption supplier legs, shared by the admin and Enterprise views.

    Copy is movement-NEUTRAL. An earlier version said "cashback spent" and "drew it down" and
    labelled the column "Amount used", which contradicts the values on screen for a RELEASE leg —
    a returned reservation INCREASES the balance. Direction is read from the Movement column,
    which carries the backend's own mutationType, not from the prose.

    AND it must not over-promise COMPLETENESS, which is a second way to mislead. The backend's
    loadLatestReserveSnapshots skips every snapshot whose mutationType is not RESERVE, so this
    column can only ever read RESERVE or the unavailable marker. An earlier revision of this note
    explained what a RELEASE would mean here, which implied the table would surface one; and the
    heading read "per-supplier movement", implying a movement log. It is not one: a recalculated
    checkout writes RESERVE/RELEASE/RESERVE under a single owner reference and only the
    reservation that finally stuck is shown. Captures, releases, corrections and superseded
    reservations are all invisible here, so the note now says so rather than letting the reader
    assume this is the whole lifecycle.

    Every data cell carries a `data-label`; the CSS turns rows into stacked cards at mobile widths
    and renders that label beside the value in place of the header row.
-->
<div class="cashback-allocation-block">
    <div class="cashback-allocation-header">Cashback Redemption &mdash; per supplier</div>

    <p class="cashback-allocation-ledger-note">
        Each row is one supplier leg of this redemption, with the trader&rsquo;s own cashback
        balance <strong>with that supplier</strong> either side of the reservation shown.
        Movement carries the backend&rsquo;s own entry type, which is why it reads RESERVE: a
        reservation, <strong>not</strong> completed spending. Later entries &mdash; a capture, a
        release, or a correction &mdash; are not shown here, so this records the reservation
        <strong>as it was taken, not its current state</strong>, and is not a full movement
        history. These are per-supplier balances &mdash; not the trader&rsquo;s main CZAR wallet
        and not the supplier&rsquo;s funding balance.
    </p>

    <div class="cashback-allocation-scroll">
        <div
            class="cashback-allocation-table redemption-leg-table"
            role="table"
            aria-label="Cashback redemption movement per supplier">
            <div class="cashback-allocation-row cashback-allocation-title" role="row">
                <div role="columnheader">Supplier</div>
                <div class="is-numeric" role="columnheader">Amount</div>
                <div class="is-numeric" role="columnheader">
                    Cashback with supplier <span class="col-head-qualifier">before</span>
                </div>
                <div class="is-numeric" role="columnheader">
                    Cashback with supplier <span class="col-head-qualifier">after</span>
                </div>
                <div role="columnheader">Movement</div>
            </div>

            @for (leg of rows; track $index) {
                <div class="cashback-allocation-row" role="row">
                    <div class="allocation-supplier" data-label="Supplier" role="cell">
                        {{ leg.supplierName || unavailableDisplay }}
                    </div>

                    <div class="is-numeric" data-label="Amount" role="cell">
                        @if (hasBalance(leg.amountUsed)) {
                            {{ balanceValue(leg.amountUsed) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div class="is-numeric" data-label="Cashback with supplier — before" role="cell">
                        @if (hasBalance(leg.balanceBefore)) {
                            {{ balanceValue(leg.balanceBefore) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div class="is-numeric" data-label="Cashback with supplier — after" role="cell">
                        @if (hasBalance(leg.balanceAfter)) {
                            {{ balanceValue(leg.balanceAfter) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div data-label="Movement" role="cell">
                        @if (leg.mutationType) {
                            <span class="allocation-status allocation-status--neutral">
                                {{ leg.mutationType }}
                            </span>
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>
                </div>
            }
        </div>
    </div>
</div>
